import axios from 'axios';
import { getCccApiBase } from '../config';
import type { CccClientType } from '../config/cccClientRoutes';

const authHeaders = () => {
  const token = localStorage.getItem('jwt_token') || localStorage.getItem('access_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export interface CccCalendarEvent {
  id: number;
  user_id: number;
  title: string;
  notes?: string | null;
  start_at: string;
  end_at: string;
  color?: string | null;
  client_type: CccClientType;
  client_ref?: string | null;
  client_label?: string | null;
  client_snapshot?: Record<string, unknown> | null;
}

export interface CccCalendarClientOption {
  client_type: CccClientType;
  client_ref: string;
  label: string;
  subtitle?: string;
  route_path: string;
  route_query?: Record<string, string>;
  snapshot?: Record<string, unknown>;
}

export type CccCalendarEventInput = {
  title: string;
  notes?: string;
  start_at: string;
  end_at: string;
  color?: string;
  client_type?: CccClientType;
  client_ref?: string;
  client_label?: string;
  client_snapshot?: Record<string, unknown>;
};

const cccCalendarApi = {
  async listEvents(from: string, to: string, userId?: number | 'all') {
    const params: Record<string, string> = { from, to };
    if (userId === 'all') {
      params.user_id = 'all';
    } else if (typeof userId === 'number' && Number.isFinite(userId)) {
      params.user_id = String(userId);
    }
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/calendar/events`, {
      headers: authHeaders(),
      params,
    });
    return data as { success: boolean; events: CccCalendarEvent[] };
  },

  async createEvent(payload: CccCalendarEventInput) {
    const { data } = await axios.post(`${getCccApiBase()}/api/ccc/calendar/events`, payload, {
      headers: authHeaders(),
    });
    return data as { success: boolean; event?: CccCalendarEvent; message?: string };
  },

  async updateEvent(id: number, payload: Partial<CccCalendarEventInput>) {
    const { data } = await axios.put(
      `${getCccApiBase()}/api/ccc/calendar/events/${id}`,
      payload,
      { headers: authHeaders() }
    );
    return data as { success: boolean; event?: CccCalendarEvent; message?: string };
  },

  async deleteEvent(id: number) {
    const { data } = await axios.delete(`${getCccApiBase()}/api/ccc/calendar/events/${id}`, {
      headers: authHeaders(),
    });
    return data as { success: boolean; message?: string };
  },

  async searchClients(q: string, type: CccClientType = 'recuperacao') {
    const { data } = await axios.get(`${getCccApiBase()}/api/ccc/calendar/clients/search`, {
      headers: authHeaders(),
      params: { q, type, limit: 25 },
    });
    return data as { success: boolean; clients: CccCalendarClientOption[] };
  },
};

export default cccCalendarApi;
